import { useCallback, useEffect, useRef, useState } from 'react';

import { prefersReducedMotion } from '@/hooks';
import { haptic } from '@/platform';

export function canSwipe(dir: -1 | 1, index: number, count: number): boolean {
  const to = index + dir;
  return to >= 0 && to <= count - 1;
}

export const EASE_PRESS = [0.22, 1, 0.36, 1] as const;

export const EASE_SLIDE = [0.4, 0, 0.2, 1] as const;

export const SWIPE = {
  commitPx: 56,
  flickPx: 30,
  flickVelocity: 500,
  fadePx: 480,
  minOpacity: 0.5,
  elastic: 0.55,
  enterPx: 96,
  enterSeconds: 0.5,
  exitShare: 0.45,
  exitMs: 200,
} as const;

export function swipeCommitted(dx: number, velocityX: number): boolean {
  const distance = Math.abs(dx);
  const flick = Math.abs(velocityX) > SWIPE.flickVelocity;
  return distance > SWIPE.commitPx || (flick && distance > SWIPE.flickPx);
}

export const swipeOpacity = (dx: number): number =>
  Math.max(SWIPE.minOpacity, 1 - Math.abs(dx) / SWIPE.fadePx);

const SLOP = 8;

export function useLongPress<T>(onFire: (item: T) => void, onRelease: () => void, ms = 400) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef({ x: 0, y: 0 });
  const fired = useRef(false);

  const clear = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => clear, [clear]);

  return useCallback(
    (item: T) => ({
      onPointerDown: (e: React.PointerEvent) => {
        if (prefersReducedMotion()) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        start.current = { x: e.clientX, y: e.clientY };
        fired.current = false;
        clear();
        timer.current = setTimeout(() => {
          fired.current = true;
          onFire(item);
        }, ms);
      },
      onPointerMove: (e: React.PointerEvent) => {
        if (timer.current === null) return;
        const dx = Math.abs(e.clientX - start.current.x);
        const dy = Math.abs(e.clientY - start.current.y);
        if (dx > SLOP || dy > SLOP) clear();
      },
      onPointerUp: () => {
        clear();
        if (fired.current) onRelease();
      },
      onPointerCancel: () => {
        clear();
        if (fired.current) onRelease();
      },
      onClickCapture: (e: React.MouseEvent) => {
        if (!fired.current) return;
        fired.current = false;
        e.preventDefault();
        e.stopPropagation();
      },
    }),
    [onFire, onRelease, ms, clear],
  );
}

export interface PullState {
  ref: (el: HTMLDivElement | null) => void;
  pillRef: React.RefObject<HTMLDivElement | null>;
  refreshing: boolean;
}

const RESIST = 0.42;
const MAX_PULL = 92;
const FIRE_AT = 50;
const SETTLE_AT = 54;

export function usePullToRefresh(onRefresh: () => void | Promise<void>): PullState {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const pillRef = useRef<HTMLDivElement | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fresh = useRef(onRefresh);
  useEffect(() => {
    fresh.current = onRefresh;
  });

  useEffect(() => {
    if (!el || prefersReducedMotion()) return;

    let from: { x: number; y: number } | null = null;
    let mode: 'none' | 'pull' | 'other' = 'none';
    let pull = 0;

    const paint = (y: number) => {
      el.style.transform = y === 0 ? '' : `translateY(${y}px)`;
      const pill = pillRef.current;
      if (pill) pill.style.opacity = String(Math.min(1, y / FIRE_AT));
    };

    const settle = () => {
      el.style.transition = 'transform .28s cubic-bezier(.22,1,.36,1)';
      paint(0);
      const pill = pillRef.current;
      if (pill) {
        pill.style.transition = 'opacity .2s';
        pill.style.opacity = '0';
      }
      window.setTimeout(() => {
        el.style.transition = '';
        el.style.willChange = '';
        if (pill) pill.style.transition = '';
      }, 300);
    };

    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      from = window.scrollY <= 0 ? { x: e.clientX, y: e.clientY } : null;
      mode = 'none';
      pull = 0;
    };

    const onMove = (e: PointerEvent) => {
      if (!from) return;
      const dx = e.clientX - from.x;
      const dy = e.clientY - from.y;
      if (mode === 'none') {
        if (Math.abs(dx) <= SLOP && Math.abs(dy) <= SLOP) return;
        mode = dy > SLOP && dy > Math.abs(dx) && window.scrollY <= 0 ? 'pull' : 'other';
        if (mode === 'pull') el.style.willChange = 'transform';
      }
      if (mode !== 'pull') return;
      pull = Math.max(0, Math.min(MAX_PULL, dy * RESIST));
      paint(pull);
    };

    const onUp = () => {
      if (!from) return;
      const wasPull = mode === 'pull';
      const reached = pull;
      from = null;
      mode = 'none';
      pull = 0;
      if (!wasPull) return;
      if (reached <= FIRE_AT) {
        settle();
        return;
      }

      setRefreshing(true);
      el.style.transition = 'transform .16s ease-out';
      paint(SETTLE_AT);
      void Promise.resolve(fresh.current())
        .catch(() => haptic.notify('error'))
        .finally(() => {
          setRefreshing(false);
          settle();
        });
    };

    el.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('pointercancel', onUp, { passive: true });
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [el]);

  return { ref: setEl, pillRef, refreshing };
}
